import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { notificationAPI } from '../services/api';
import { FiHome, FiActivity, FiFileText, FiUsers, FiClipboard, FiBarChart2, FiHeart, FiShield, FiSettings, FiBell, FiLogOut, FiMenu, FiX, FiChevronDown, FiSearch, FiSun, FiMoon, FiTarget, FiLayers, FiTrendingUp, FiDatabase, FiList, FiGlobe, FiEdit, FiExternalLink, FiServer } from 'react-icons/fi';

const navItems = [
  { path: '/dashboard', icon: FiHome, label: 'Dashboard', roles: 'all', feature: 'dashboard' },
  { path: '/surveillance', icon: FiActivity, label: 'Disease Surveillance', roles: 'all', feature: 'surveillance' },
  { path: '/assessments', icon: FiClipboard, label: 'Assessments', roles: 'all', feature: 'assessments' },
  { path: '/programs', icon: FiTarget, label: 'Health Programs', roles: 'all', feature: 'programs' },
  { path: '/vaccinations', icon: FiShield, label: 'Vaccinations', roles: 'all', feature: 'vaccinations' },
  { path: '/vitals', icon: FiHeart, label: 'Vital Statistics', roles: 'all', feature: 'vitals' },
  { path: '/sdoh', icon: FiLayers, label: 'SDOH Tracker', roles: 'all', feature: 'sdoh' },
  { path: '/health-equity', icon: FiTrendingUp, label: 'Health Equity', roles: 'all', feature: 'health-equity' },
  { path: '/reports', icon: FiBarChart2, label: 'Reports', roles: 'all', feature: 'reports' },
  { path: '/ai-insights', icon: FiSun, label: 'AI Insights', roles: 'all', feature: 'ai-insights' },
  { path: '/population', icon: FiDatabase, label: 'Population Data', roles: 'all', feature: 'population' },
  { path: '/admin/users', icon: FiUsers, label: 'User Management', roles: ['super_admin', 'admin'], feature: null },
  { path: '/admin/workspaces', icon: FiGlobe, label: 'Workspaces', roles: ['super_admin'], feature: null },
  { path: '/admin/blog', icon: FiEdit, label: 'Blog Manager', roles: ['super_admin'], feature: null },
  { path: '/audit-logs', icon: FiList, label: 'Audit Logs', roles: ['super_admin', 'admin'], feature: null },
  { path: '/admin/system-health', icon: FiServer, label: 'System Health', roles: ['super_admin'], feature: null },
  { path: '/settings', icon: FiSettings, label: 'Settings', roles: 'all', feature: 'settings' },
];

const Layout = ({ children }) => {
  const { user, logout, hasFeature } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifCount, setNotifCount] = useState(0);
  const [showNotifDrop, setShowNotifDrop] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showUserMenu, setShowUserMenu] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) { setSidebarOpen(false); }
      else { setMobileOpen(false); setSidebarOpen(true); }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const loadNotifs = async () => {
      try {
        const { data } = await notificationAPI.getAll({ limit: 5 });
        setNotifCount(data.unreadCount || 0);
        setNotifications(data.notifications || []);
      } catch {}
    };
    loadNotifs();
    const interval = setInterval(loadNotifs, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close mobile sidebar on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const filteredNav = navItems.filter(item => {
    const roleOk = item.roles === 'all' || item.roles.includes(user?.role);
    const featureOk = !item.feature || hasFeature(item.feature);
    return roleOk && featureOk;
  });

  const roleColors = {
    super_admin: '#dc2626', admin: '#7c3aed', analyst: '#2563eb',
    field_worker: '#059669', viewer: '#6b7280'
  };

  const roleBadge = {
    super_admin: 'Super Admin', admin: 'Admin', analyst: 'Analyst',
    field_worker: 'Field Worker', viewer: 'Viewer'
  };

  const markAllRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      setNotifCount(0);
      setNotifications(n => n.map(x => ({ ...x, isRead: true })));
    } catch {}
  };

  // Theme-aware colors
  const t = {
    headerBg: isDark ? '#111827' : '#fff',
    headerBorder: isDark ? '#1e293b' : '#e2e8f0',
    headerShadow: isDark ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.05)',
    menuBg: isDark ? '#111827' : '#fff',
    menuBorder: isDark ? '#1e293b' : '#e2e8f0',
    menuShadow: isDark ? '0 20px 60px rgba(0,0,0,0.4)' : '0 20px 60px rgba(0,0,0,0.15)',
    menuHover: isDark ? '#1e293b' : '#f1f5f9',
    textPrimary: isDark ? '#f1f5f9' : '#334155',
    textMuted: isDark ? '#94a3b8' : '#64748b',
    iconColor: isDark ? '#94a3b8' : '#64748b',
    pageBg: isDark ? '#0a0f1e' : '#f0f4f8',
    wsBg: isDark ? 'rgba(37,99,235,0.1)' : '#f0f9ff',
    wsBorder: isDark ? 'rgba(59,130,246,0.3)' : '#bfdbfe',
    notifUnread: isDark ? 'rgba(37,99,235,0.1)' : '#f0f9ff',
    notifRead: isDark ? '#111827' : '#fff',
    notifHover: isDark ? '#1e293b' : '#f8fafc',
  };

  const showSidebar = isMobile ? mobileOpen : true;
  const sidebarWidth = isMobile ? 260 : (sidebarOpen ? 260 : 72);
  const contentMargin = isMobile ? 0 : (sidebarOpen ? 260 : 72);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: t.pageBg }}>
      {/* Sidebar — always dark */}
      <aside className={`layout-sidebar${mobileOpen ? ' mobile-open' : ''}`} style={{
        width: sidebarWidth, background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
        color: '#e2e8f0', transition: 'width 0.3s', position: 'fixed', top: 0, left: 0, bottom: 0,
        zIndex: 100, overflowY: 'auto', overflowX: 'hidden',
        display: showSidebar ? 'block' : 'none'
      }}>
        {/* Logo */}
        <div style={{ padding: '20px 16px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', gap: 12, minHeight: 72 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg, #00d4aa, #00b894)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 16, color: '#020617', flexShrink: 0, fontFamily: "'DM Serif Display', serif" }}>ES</div>
          {(sidebarOpen || isMobile) && <div><div style={{ fontWeight: 700, fontSize: 15 }}>EpiSurveillance</div><div style={{ fontSize: 10, color: '#00d4aa', fontWeight: 600, letterSpacing: '1px' }}>Disease Surveillance</div></div>}
        </div>

        {/* Nav */}
        <nav style={{ padding: '12px 8px' }}>
          {filteredNav.map(item => {
            const active = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            const Icon = item.icon;
            return (
              <Link key={item.path} to={item.path} onClick={() => setMobileOpen(false)} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', marginBottom: 2,
                borderRadius: 8, textDecoration: 'none', transition: 'all 0.2s',
                background: active ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
                color: active ? '#60a5fa' : '#94a3b8',
                fontWeight: active ? 600 : 400, fontSize: 14
              }}>
                <Icon size={20} style={{ flexShrink: 0 }} />
                {(sidebarOpen || isMobile) && <span>{item.label}</span>}
                {active && <div style={{ width: 3, height: 20, background: '#3b82f6', borderRadius: 2, marginLeft: 'auto' }} />}
              </Link>
            );
          })}
        </nav>

        {/* User Card */}
        {(sidebarOpen || isMobile) && (
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: 16, borderTop: '1px solid #334155', background: '#0f172a' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: roleColors[user?.role] || '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 14 }}>
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.firstName} {user?.lastName}</div>
                <div style={{ fontSize: 11, color: roleColors[user?.role], fontWeight: 500 }}>{roleBadge[user?.role]}</div>
              </div>
            </div>
          </div>
        )}
      </aside>

      {/* Main Content */}
      <div className="layout-main" style={{ flex: 1, marginLeft: contentMargin, transition: 'margin 0.3s' }}>
        {/* Top Bar */}
        <header className="layout-topbar" style={{
          height: 64, background: t.headerBg, borderBottom: `1px solid ${t.headerBorder}`,
          display: 'flex', alignItems: 'center', padding: '0 24px', gap: 16,
          position: 'sticky', top: 0, zIndex: 50, boxShadow: t.headerShadow,
          transition: 'background 0.25s, border-color 0.25s'
        }}>
          <button onClick={() => { if (isMobile) setMobileOpen(!mobileOpen); else setSidebarOpen(!sidebarOpen); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.iconColor, padding: 4 }}>
            {mobileOpen ? <FiX size={22} /> : <FiMenu size={22} />}
          </button>

          {/* Workspace Indicator */}
          {user?.workspace && (
            <div className="topbar-workspace" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', background: t.wsBg, borderRadius: 8, border: `1px solid ${t.wsBorder}`, transition: 'background 0.25s' }}>
              <FiGlobe size={14} color="#2563eb" />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#3b82f6' }}>{user.workspace.name}</span>
              <span style={{ fontSize: 11, color: '#60a5fa', fontWeight: 500, textTransform: 'capitalize' }}>({user.workspace.type})</span>
            </div>
          )}

          <div style={{ flex: 1 }} />

          {/* Theme Toggle */}
          <button onClick={toggleTheme} title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            style={{
              background: isDark ? 'rgba(250,204,21,0.1)' : 'rgba(99,102,241,0.08)',
              border: `1px solid ${isDark ? 'rgba(250,204,21,0.2)' : 'rgba(99,102,241,0.15)'}`,
              cursor: 'pointer', padding: '8px', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.3s',
              color: isDark ? '#facc15' : '#6366f1'
            }}>
            {isDark ? <FiSun size={18} /> : <FiMoon size={18} />}
          </button>

          {/* Notifications */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => { setShowNotifDrop(!showNotifDrop); setShowUserMenu(false); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.iconColor, padding: 8, position: 'relative' }}>
              <FiBell size={20} />
              {notifCount > 0 && (
                <span style={{ position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%', background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{notifCount}</span>
              )}
            </button>

            {showNotifDrop && (
              <div className="notif-dropdown" style={{ position: 'absolute', top: '100%', right: 0, width: 360, background: t.menuBg, borderRadius: 12, boxShadow: t.menuShadow, border: `1px solid ${t.menuBorder}`, zIndex: 1000, transition: 'background 0.25s' }}>
                <div style={{ padding: '16px 20px', borderBottom: `1px solid ${t.menuBorder}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 700, fontSize: 15, color: t.textPrimary }}>Notifications</span>
                  {notifCount > 0 && <button onClick={markAllRead} style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: 13 }}>Mark all read</button>}
                </div>
                <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                  {notifications.length === 0 ? (
                    <div style={{ padding: 40, textAlign: 'center', color: t.textMuted }}>No notifications</div>
                  ) : notifications.map(n => (
                    <div key={n._id} style={{ padding: '12px 20px', borderBottom: `1px solid ${isDark ? '#1e293b' : '#f1f5f9'}`, background: n.isRead ? t.notifRead : t.notifUnread, cursor: 'pointer' }}
                      onClick={() => { if (n.link) navigate(n.link); setShowNotifDrop(false); }}>
                      <div style={{ fontSize: 13, fontWeight: n.isRead ? 400 : 600, color: t.textPrimary }}>{n.title}</div>
                      <div style={{ fontSize: 12, color: t.textMuted, marginTop: 4 }}>{n.message?.substring(0, 80)}...</div>
                      <div style={{ fontSize: 11, color: isDark ? '#475569' : '#94a3b8', marginTop: 4 }}>{new Date(n.createdAt).toLocaleDateString()}</div>
                    </div>
                  ))}
                </div>
                <Link to="/notifications" onClick={() => setShowNotifDrop(false)} style={{ display: 'block', padding: 12, textAlign: 'center', color: '#3b82f6', fontSize: 13, textDecoration: 'none', borderTop: `1px solid ${t.menuBorder}` }}>View All</Link>
              </div>
            )}
          </div>

          {/* User Menu */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => { setShowUserMenu(!showUserMenu); setShowNotifDrop(false); }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', padding: '6px 12px', borderRadius: 8 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: roleColors[user?.role] || '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 12 }}>
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              <span className="topbar-username" style={{ fontSize: 14, fontWeight: 500, color: t.textPrimary }}>{user?.firstName}</span>
              <FiChevronDown size={14} color={t.textMuted} />
            </button>

            {showUserMenu && (
              <div style={{ position: 'absolute', top: '100%', right: 0, width: 210, background: t.menuBg, borderRadius: 10, boxShadow: t.menuShadow, border: `1px solid ${t.menuBorder}`, zIndex: 1000, padding: 8 }}>
                <a href="/" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 6, textDecoration: 'none', color: t.textPrimary, fontSize: 14 }}>
                  <FiExternalLink size={16} /> Visit Website
                </a>
                <Link to="/settings" onClick={() => setShowUserMenu(false)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 6, textDecoration: 'none', color: t.textPrimary, fontSize: 14 }}>
                  <FiSettings size={16} /> Settings
                </Link>
                <div style={{ borderTop: `1px solid ${t.menuBorder}`, margin: '4px 0' }} />
                <button onClick={() => { logout(); navigate('/login'); }} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 6, background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 14, width: '100%' }}>
                  <FiLogOut size={16} /> Logout
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Page Content */}
        <main className="layout-page" style={{ padding: 24, minHeight: 'calc(100vh - 64px)', background: t.pageBg, transition: 'background 0.25s' }}>
          {children}
        </main>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && <div onClick={() => setMobileOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 99 }} />}
    </div>
  );
};

export default Layout;
